import React, { useMemo, useState } from "react";
import { Search, RotateCw, SlidersHorizontal, Eraser, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import CertificateToolbar from "./CertificateToolbar";

const STATUS_OPTIONS = [
  { value: "all", label: "Tất cả trạng thái" },
  { value: "pending_hq", label: "Chờ duyệt chuyên môn" },
  { value: "pending_mgmt", label: "Chờ duyệt lãnh đạo" },
  { value: "completed", label: "Hoàn thành" },
  { value: "rejected", label: "Từ chối" },
];

const ActionButton = ({ icon: Icon, onClick, title, active = false, spinning = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
      active
        ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
        : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
    }`}
  >
    <Icon size={18} className={spinning ? "animate-spin" : ""} />
  </button>
);

const SelectBox = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-1.5 w-full">
    <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
      {label}
    </label>
    <div className="relative group">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
      />
    </div>
  </div>
);

const CertificateRequestFilter = ({
  searchInput,
  onSearchChange,
  status,
  onStatusChange,
  mineOnly,
  onMineOnlyChange,
  onRefresh,
  loading = false,
  onTemplates,
  showTemplates = true,
  showCreateRequestButton = false,
  onOpenRequestPicker,
}) => {
  const [advanced, setAdvanced] = useState(false);

  const isFiltering = useMemo(
    () => Boolean(String(searchInput || "").trim()) || status !== "all" || Boolean(mineOnly),
    [searchInput, status, mineOnly]
  );

  const clearFilters = () => {
    onSearchChange("");
    onStatusChange("all");
    onMineOnlyChange(false);
  };

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative w-full max-w-xl group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm lớp, chi nhánh, khóa học, người gửi, email hoặc mã yêu cầu..."
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton
            icon={RotateCw}
            onClick={onRefresh}
            title="Làm mới"
            spinning={loading}
          />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced((prev) => !prev)}
            active={advanced}
            title="Lọc nâng cao"
          />

          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all whitespace-nowrap"
            >
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        <CertificateToolbar
          variant="requests"
          onTemplates={onTemplates}
          showTemplates={showTemplates}
          showCreateRequestButton={showCreateRequestButton}
          onOpenRequestPicker={onOpenRequestPicker}
          className="w-full xl:w-auto xl:justify-end border-t border-gray-100 pt-4 xl:border-t-0 xl:pt-0"
        />
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Trạng thái"
                value={status}
                options={STATUS_OPTIONS}
                onChange={onStatusChange}
              />

              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Phạm vi hiển thị
                </label>
                <button
                  type="button"
                  onClick={() => onMineOnlyChange(!mineOnly)}
                  className={`h-[50px] rounded-2xl border px-4 text-left transition-all shadow-sm ${
                    mineOnly
                      ? "border-red-200 bg-red-50 text-red-700"
                      : "border-gray-200 bg-white text-gray-700 hover:border-red-300"
                  }`}
                >
                  <span className="block text-sm font-bold">
                    {mineOnly ? "Chỉ yêu cầu của tôi" : "Tất cả yêu cầu"}
                  </span>
                  <span className="block mt-0.5 text-[11px] text-gray-500">
                    {mineOnly
                      ? "Đang giới hạn theo người gửi hiện tại"
                      : "Hiển thị cả yêu cầu của người khác nếu có quyền"}
                  </span>
                </button>
              </div>

              <div className="flex items-end">
                <div className="w-full rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                    Gợi ý tìm nhanh
                  </p>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                    Có thể tìm theo mã yêu cầu, tên lớp, tên chi nhánh, email người gửi hoặc tên mẫu chứng nhận.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CertificateRequestFilter;
